/**
 * AnnouncementBar Component
 * Slim promotional bar rendered above the header.
 * Supports optional inline link and dismissible behaviour.
 */

'use client';

import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { IconButton, useTheme } from '@template-project/afrodita-components';
import { X } from 'lucide-react';
import { isAllowedHref } from '@/lib/utils/is-allowed-href';
import { useTranslation } from '@/lib/i18n';
import {
  ANNOUNCEMENT_BAR_ACCESSIBILITY_DISMISS,
  ANNOUNCEMENT_BAR_ACCESSIBILITY_REGION,
} from './i18.keys';

// --------------------------------------------------------------------------
// Prop types
// --------------------------------------------------------------------------

interface AnnouncementBarProps {
  message: string;
  backgroundColor?: string;
  textColor?: string;
  linkText?: string;
  /** Full href for the inline link. For internal paths in localized storefronts,
   *  pass the already-localized URL (e.g. via `localizedPath('/products')`).
   *  Only http(s), relative paths, and hash links are allowed. */
  linkHref?: string;
  dismissible?: boolean;
}

// --------------------------------------------------------------------------
// Constants
// --------------------------------------------------------------------------

const ANNOUNCEMENT_BAR_HEIGHT = 36;

// --------------------------------------------------------------------------
// Styled components
// --------------------------------------------------------------------------

const Bar = styled.div<{ $backgroundColor: string; $textColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${ANNOUNCEMENT_BAR_HEIGHT}px;
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.xl};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ $textColor }) => $textColor};
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  line-height: 1.4;
  text-align: center;
`;

const MessageText = styled.span`
  /* inherits color from Bar */
`;

const InlineLink = styled.a<{ $textColor: string }>`
  color: ${({ $textColor }) => $textColor};
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid ${({ $textColor }) => $textColor};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const DismissButton = styled(IconButton)<{ $textColor: string }>`
  position: absolute;
  right: ${({ theme }) => theme.space.md};
  top: 50%;
  transform: translateY(-50%);
  padding: ${({ theme }) => theme.space.xs};
  color: ${({ $textColor }) => $textColor};
  border-radius: 2px;

  &:focus-visible {
    outline: 2px solid ${({ $textColor }) => $textColor};
    outline-offset: 2px;
  }
`;

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

function hashMessage(msg: string): string {
  let hash = 0;
  for (let i = 0; i < msg.length; i++) {
    hash = ((hash << 5) - hash + msg.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

export function AnnouncementBar({
  message,
  backgroundColor,
  textColor,
  linkText,
  linkHref,
  dismissible = false,
}: AnnouncementBarProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const storageKey = `announcement-dismissed-${hashMessage(message)}`;

  // Lazy initializer reads sessionStorage on mount (client only).
  // On the server (typeof window === 'undefined') this returns false,
  // matching the server-rendered HTML so there is no hydration mismatch
  // when the bar is NOT dismissed. When it IS dismissed the bar disappears
  // on the very first client render — no flash.
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return sessionStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });

  // Re-check when message (and therefore storageKey) changes while mounted
  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(storageKey) === 'true');
    } catch { /* Safari private mode — treat as not dismissed */ }
  }, [storageKey]);

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(storageKey, 'true'); } catch { /* storage unavailable */ }
  };

  const resolvedBackground = backgroundColor ?? theme.colors.pallete.primary.color;
  const resolvedTextColor = textColor ?? theme.colors.pallete.primary.contrast;

  if (dismissible && dismissed) return null;

  const hasLink = Boolean(linkText && linkHref && isAllowedHref(linkHref!));

  return (
    <Bar
      role="region"
      aria-label={t(ANNOUNCEMENT_BAR_ACCESSIBILITY_REGION)}
      $backgroundColor={resolvedBackground}
      $textColor={resolvedTextColor}
    >
      <Content>
        <MessageText>{message}</MessageText>
        {hasLink && (
          <InlineLink
            href={linkHref}
            rel="noopener noreferrer"
            $textColor={resolvedTextColor}
          >
            {linkText}
          </InlineLink>
        )}
      </Content>

      {dismissible && (
        <DismissButton
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t(ANNOUNCEMENT_BAR_ACCESSIBILITY_DISMISS)}
          onClick={handleDismiss}
          $textColor={resolvedTextColor}
          icon={<X size={14} aria-hidden />}
        />
      )}
    </Bar>
  );
}

export default AnnouncementBar;
